import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  buildArraySorter,
  buildQueryFilters,
  useGetTeamsInfiniteList,
} from '@sib/api';
import {
  DEFAULT_PAGE,
  MAXIMUM_PAGINATION,
  DEFAULT_STALE_TIME,
  DEFAULT_PAGE_SIZE,
} from '@sib/api/constants';
import { useTranslation } from '@sib/i18n';
import { ComboboxAvatarItem, ComboboxAvatarItemOption } from '@sib/ui';
import { getInitials, useDebouncedValue } from '@sib/utils';

interface TeamsAutocompleteOptions {
  additionalFields?: ComboboxAvatarItemOption[];
}

export const getTeamsListFilter = (searchTerm: string, size?: number) => ({
  page: DEFAULT_PAGE,
  withUsers: true,
  size: size || MAXIMUM_PAGINATION,
  sort: buildArraySorter({
    field: 'name',
    order: 'asc',
  }),

  filter: searchTerm
    ? buildQueryFilters([
        {
          field: 'name',
          value: searchTerm,
          operator: 'contains',
        },
      ])
    : [],
});

export const useTeamsAutocomplete = ({
  additionalFields,
}: TeamsAutocompleteOptions) => {
  const { t } = useTranslation();

  const [filter, setFilter] = useState<string>('');

  const debouncedfilter = useDebouncedValue(filter, 500);

  const apiFilter = useMemo(
    () => getTeamsListFilter(debouncedfilter, DEFAULT_PAGE_SIZE),
    [debouncedfilter]
  );

  const {
    data: teams,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    error,
  } = useGetTeamsInfiniteList(apiFilter, {
    staleTime: DEFAULT_STALE_TIME,
  });

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage,
    onLoadMore: () => !isFetchingNextPage && fetchNextPage(),
    disabled: !!error,
    rootMargin: '0px 0px 0px 0px',
  });

  const teamsItems = useMemo(
    () =>
      teams?.pages
        ? (teams.pages || [])
            .map(item => item.items)
            .flat()
            .map(team => ({
              label: team.name,
              value: team.uuid,
              initial: getInitials(team.name),
              avatarUrl: team.teamLogo,
            }))
            .filter(
              item => !additionalFields?.map(i => i.value).includes(item.value)
            )
        : [],
    [teams, additionalFields]
  );

  const options = useMemo(
    () => [...(additionalFields || []), ...teamsItems],
    [teamsItems, additionalFields]
  );

  const onInputValueChange = useCallback(
    (value: string) => {
      setFilter(value);
    },
    [setFilter]
  );

  return {
    disabled: isLoading,
    isLoading: isLoading,
    emptyMessage: t('notSelected'),
    noResultMessage: t('noResults'),
    searchPlaceholder: t(
      'components.subAuditForm.sections.details.fields.team.searchPlaceholder'
    ),
    clearText: t('clear'),
    options,
    hasNextPage,
    sentryRef,
    onInputValueChange,
    OptionComponent: ComboboxAvatarItem,
  };
};
